import Row from "react-bootstrap/Row"
import Button from "react-bootstrap/Button"
import Container from "react-bootstrap/Container"
import Form from 'react-bootstrap/Form'
import "../styles/Login.css"
import { useEffect, useState } from "react"
import Alert from 'react-bootstrap/Alert'
import { useTranslation } from "react-i18next"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css" // Import Quill styles

// import articleService from "../services/articleService"
import { useNavigate } from "react-router-dom"
import "../styles/ArticleForm.css"
import eventService from "../services/eventService"
import newsService from "../services/newsService"
import { BASE_SERVER_URL } from "../constants"

const MAX_LENGTH_ARTICLE_FULL_TEXT = 10000

/**
 * Component for creating, updating article, event or news. 
 * Article is always required
 * Must have either event or news, both are not possible
 */
const ArticleForm = ({ article, event, news, editMode, setEditMode }) => {
    // article fields
    const [title, setTitle] = useState(null)
    const [text, setText] = useState(null)
    // news fields
    const [summary, setSummary] = useState(null)
    // event fields
    const [eventName, setEventName] = useState(null)
    const [eventDate, setEventDate] = useState(null)
    const [eventVenue, setEventVenue] = useState(null)
    const [file, setFile] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [pageType, setPageType] = useState(null)
    const [typeSelectionDisabled, setTypeSelectionDisabled] = useState(null)
    const [eventType, setEventType] = useState(null)
    const [error, setError] = useState(null)
    const { t } = useTranslation()
    const navigate = useNavigate()

    // ask user's permission before saving changes
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            // Prevent the default action to display the alert message
            event.preventDefault()

            // Set a custom message for the confirmation alert
            event.returnValue = "Are you sure you want to leave? Your changes may not be saved."

            // For older browsers compatibility
            return "Are you sure you want to leave? Your changes may not be saved."
        }

        // Add the event listener when the component mounts
        window.addEventListener('beforeunload', handleBeforeUnload)

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [])


    useEffect(() => {
        if (article) {
            setTitle(article.title)
            setText(article.fullText)
        }

        // if we are not creating new article, set existing page info
        if (!editMode) {
            if (event) {
                setPageType("event")
            } else if (news) {
                setPageType("news")
            } else {
                setPageType("plain")
            }
        } else if (event) {
            setEventName(event.name)
            setEventVenue(event.venue)
            setEventDate(event.organizedOn)
            setPageType("event")
            setEventType(event.eventType ? event.eventType : "EVENT_TYPE_UNSPECIFIED")
            setTypeSelectionDisabled(true)
        } else if (news) {
            setSummary(news.summary)
            setPageType("news")
            setTypeSelectionDisabled(true)
        } else {
            setPageType("plain")
            setTypeSelectionDisabled(true)
        }
    }, [])

    const handleArticleSubmit = async (e) => {
        e.preventDefault()
        setError(null)

        // Add a confirmation prompt before proceeding
        const isConfirmed = window.confirm(editMode ? t("article_page_edit_article_confirmation") : t("article_page_create_article_confirmation"))

        if (!isConfirmed) {
            // If the user cancels, exit the function
            return
        }

        const newArticle = {
            title: title,
            fullText: text
        }

        if (article) {
            newArticle.createdDate = article.createdDate
            newArticle.modifiedDate = article.modifiedDate
        }

        if (editMode) {
            if (pageType === "plain") {
                newArticle._id = article._id
            }

            newArticle.modifiedDate = Date.now()

            // edit article, event or news
            let retPromise
            switch (pageType) {
                case "event":
                    retPromise = eventService.updateEvent(event._id, eventName, eventDate, eventVenue, file, event.fileUrl, eventType, newArticle)
                    break
                case "news":
                    retPromise = newsService.updateNews(news._id, summary, newArticle)
                    break
                // case "plain":
                //     retPromise = articleService.updateArticle(newArticle)
                //     break
            }

            retPromise
                .then((ret) => {
                    if (ret.response && ret.response.status === 304) {
                        setError(t("article_form_not_modified_message"))
                    }
                    else if (ret.response && ret.response.status !== 200) {
                        setError("Editing article failed. " + ret.response.status)
                    }
                    else {
                        alert(t("article_form_article_edited_message"))
                        setEditMode(false)
                        setTimeout(() => {
                            window.location.reload()
                        }, 100)
                    }
                }).catch(err => setError(err.message))


        } else {
            // create new article, event or news
            const now = Date.now()
            newArticle.createdDate = now
            newArticle.modifiedDate = now
            let retPromise
            switch (pageType) {
                case "event":
                    retPromise = eventService.postEvent(eventName, eventDate, eventVenue, file, eventType, newArticle)
                    break
                case "news":
                    retPromise = newsService.postNews(summary, newArticle)
                    break
                // case "plain":
                //     retPromise = articleService.postArticle(newArticle)
                //     break
            }

            retPromise
                .then(ret => {
                    if (ret.response && ret.response.status !== 200) {
                        setError(t("article_form_creating_article_failed_error") + ret.response.status)
                    } else {
                        alert(t("article_form_create_alert_message"))
                        navigate("/")
                        setTimeout(() => {
                            window.location.reload()
                        }, 100)
                    }
                }).catch(err => console.error(err))
        }
    }

    const handlePageTypeSelect = (e) => {
        const type = e.target.value
        if (["event", "news", "plain"].includes(type)) {
            setPageType(type)
        } else {
            throw TypeError("Unknown page type")
        }
    }

    const handleEventTypeSelect = (e) => {
        const type = e.target.value
        if (["GAME_NIGHT", "EVENT_TYPE_UNSPECIFIED"].includes(type)) {
            setEventType(type)
        } else {
            throw TypeError("Unknown event type")
        }
    }

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0]
        setFile(selectedFile)

        // Generate a preview URL for the selected image
        if (selectedFile) {
            setPreviewUrl(URL.createObjectURL(selectedFile))
        } else {
            setPreviewUrl(null)
        }
    }

    const toDateTimeLocalValue = (dateStr) => {
        const d = new Date(dateStr);
        console.log("toDateTimeLocalValue:", dateStr)
        // Check if the Date object is valid
        if (isNaN(d.getTime())) {
            console.error("getDateString: Invalid Date object created.")
            return ""
        }
        // Pad month, day, hours, minutes with leading zeros
        const pad = (n) => n.toString().padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };


    return (
        <Row>
            <Container className="BREIKKI-article-form-container">
                <Form onSubmit={e => handleArticleSubmit(e)}>
                    <div key="radio-group" className="mb-3">
                        {t("article_form_page_type_select_title")}
                        <Form.Check
                            type="radio"
                            label={t("article_form_page_type_select_event")}
                            name="pageType"
                            value="event"
                            onChange={handlePageTypeSelect}
                            required
                            disabled={typeSelectionDisabled}
                            defaultChecked={typeSelectionDisabled && pageType === "event"}
                        />
                        <Form.Check
                            type="radio"
                            label={t("article_form_page_type_select_news")}
                            name="pageType"
                            value="news"
                            onChange={handlePageTypeSelect}
                            required
                            disabled={typeSelectionDisabled}
                            defaultChecked={typeSelectionDisabled && pageType === "news"}
                        />
                        {/* TODO: commented out for now, no need for plain articles yet */}
                        {/* <Form.Check
                            type="radio"
                            label={t("article_form_page_type_select_plain")}
                            name="pageType"
                            value="plain"
                            onChange={handlePageTypeSelect}
                            required
                            disabled={typeSelectionDisabled}
                            defaultChecked={typeSelectionDisabled && pageType === "plain"}
                        /> */}
                    </div>

                    {pageType === "event" &&
                        <>
                            <Form.Group controlId="formEventName">
                                <Form.Label>{t("article_form_event_name_input_label")}</Form.Label>
                                <Form.Control type="text" value={eventName}
                                    onChange={(e) => setEventName(e.target.value)}
                                    minLength={1}
                                    maxLength={100}
                                    required
                                />
                            </Form.Group>
                            <Form.Group controlId="formEventVenue">
                                <Form.Label>{t("article_form_event_venue_input_label")}</Form.Label>
                                <Form.Control type="text" value={eventVenue}
                                    onChange={(e) => setEventVenue(e.target.value)}
                                    minLength={1}
                                    maxLength={100}
                                    required
                                />
                            </Form.Group>
                            <Form.Group controlId="eventDateInput">
                                <Form.Label>{t("article_form_event_date_input_label")}</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    onChange={(e) => setEventDate(e.target.value)}
                                    value={
                                        eventDate
                                            ? toDateTimeLocalValue(eventDate)
                                            : event && event.organizedOn
                                                ? toDateTimeLocalValue(event.organizedOn)
                                                : ""
                                    }
                                    required
                                />
                            </Form.Group>
                            <div key="event-type-radio-group" className="mb-3">
                                {t("article_form_event_type_select_title")}
                                <Form.Check
                                    type="radio"
                                    label={t("article_form_event_type_select_option_game_night")}
                                    name="eventType"
                                    value="GAME_NIGHT"
                                    onChange={handleEventTypeSelect}
                                    required
                                    defaultChecked={eventType === "GAME_NIGHT"}
                                />
                                <Form.Check
                                    type="radio"
                                    label={t("article_form_event_type_select_option_unspecified")}
                                    name="eventType"
                                    value="EVENT_TYPE_UNSPECIFIED"
                                    onChange={handleEventTypeSelect}
                                    required
                                    defaultChecked={eventType === "EVENT_TYPE_UNSPECIFIED"}
                                />
                            </div>
                        </>
                    }

                    <Form.Group className="mb-3" controlId="formTitle">
                        <Form.Label>{t("article_form_title_input_label")}</Form.Label>
                        <Form.Control type="text" value={title} placeholder={t("article_form_title_placeholder")}
                            onChange={(e) => setTitle(e.target.value)}
                            minLength={1}
                            maxLength={100}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formFullText">
                        <Form.Label>{t("article_form_full_text_input_label")}</Form.Label>
                        <ReactQuill
                            value={text}
                            onChange={(value) => {
                                console.log(value.length)
                                if (value.length > MAX_LENGTH_ARTICLE_FULL_TEXT) {
                                    setError(t("article_form_error_max_length"))
                                } else {
                                    setError(null)
                                }
                                setText(value.substring(0, MAX_LENGTH_ARTICLE_FULL_TEXT))
                            }}
                            placeholder={t("article_form_full_text_placeholder")}
                            modules={{
                                toolbar: [
                                    [{ header: [1, 2, false] }],
                                    ["bold", "italic", "underline", "strike"],
                                    [{ list: "ordered" }, { list: "bullet" }],
                                    ["link"],
                                    ["clean"],
                                ],
                            }}
                            formats={[
                                "header",
                                "bold",
                                "italic",
                                "underline",
                                "strike",
                                "list",
                                "bullet",
                                "link",
                                "image",
                            ]}
                        />
                    </Form.Group>

                    {pageType === "news" &&
                        <Form.Group className="mb-3" controlId="formSummary">
                            <Form.Label>{t("article_form_summary_input_label")}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                type="text"
                                value={summary}
                                placeholder={t("article_form_summary_placeholder")}
                                onChange={(e) => setSummary(e.target.value)}
                                maxLength={200}
                                required
                            />
                        </Form.Group>
                    }

                    {pageType === "event" &&
                        <Form.Group className="mb-3" controlId="formImageFile">
                            <Form.Label>
                                <div className="BREIKKI-article-form-preview-imgs-container">
                                    <span>
                                        {t("article_form_file_input_label")}
                                    </span>
                                    {editMode && event.fileUrl && !previewUrl &&
                                        <img src={`${BASE_SERVER_URL}${event.fileUrl}`} className="BREIKKI-article-form-preview-img" />
                                    }
                                    {previewUrl &&
                                        <img src={`${previewUrl}`} className="BREIKKI-article-form-preview-img" />
                                    }
                                </div>
                            </Form.Label>
                            <Form.Control type="file" onChange={handleFileChange} />
                        </Form.Group>
                    }

                    {error && (
                        <Alert variant="danger" dismissible>
                            {error}
                        </Alert>)
                    }
                    <Button variant="success" type="submit" className="BREIKKI-article-form-save-button">
                        {editMode ? t("article_form_save_changes") : t("article_form_create_article_title")}
                    </Button>
                </Form>
            </Container>
        </Row>
    )
}

export default ArticleForm